import {
  AnalyticsBrowser as AnalyticsBrowserCore,
  type Options,
} from '@segment/analytics-next';

import {
  formatBaseContext,
  formatIndividualContext,
} from '../core/format/format-context';
import { formatIdentify } from '../core/format/format-identify';
import { formatTrack } from '../core/format/format-track';
import { Email } from '../core/primitives';
import { TrackingEvent, TrackingEventProperties } from '../core/tracking-plan';
import type {
  TranscendGroupTraits,
  TranscendIndividualTraits,
} from '../core/types';

export class BrowserAnalytics {
  public core: AnalyticsBrowserCore;

  constructor() {
    this.core = new AnalyticsBrowserCore();
  }

  /**
   * Get the email from local storage, if it exists
   */
  public async getEmail(): Promise<string | undefined> {
    const user = await this.core.user();
    const traits = user.traits();
    const result = await Email.safeParseAsync(traits?.email);
    return result.data;
  }

  public async identify(
    email: string,
    traits?: TranscendIndividualTraits,
    options?: Omit<Options, 'userId' | 'traits'> & { email: string },
  ): ReturnType<AnalyticsBrowserCore['identify']> {
    const { formattedUserId, formattedTraits } = await formatIdentify({
      email,
      traits,
    });

    // Alias the current anonymous ID to the new identified userId so that
    // downstream tools (e.g. PostHog) merge the anonymous and identified
    // profiles. Only alias on first identification—skip if already identified.
    const user = await this.core.user();
    // Clear stale userIds left by a prior bug that identified with anonymousId.
    // Do not use reset() — that would mint a new anonymousId and orphan history.
    const existingUserId = user.id();
    const anonymousId = user.anonymousId();
    if (existingUserId && anonymousId && existingUserId === anonymousId) {
      user.id(null);
    }
    if (!user.id() && user.anonymousId()) {
      await this.core.alias(formattedUserId);
    }

    // Format the options object
    const formattedOptions = {
      ...options,
      context: formatIndividualContext({
        context: options?.['context'],
        formattedTraits,
      }),
    };

    return this.core.identify(
      formattedUserId,
      formattedTraits,
      formattedOptions,
    );
  }

  public async track<
    T extends TrackingEvent,
    P extends TrackingEventProperties[T],
  >(
    event: T,
    properties?: P,
    options?: Omit<Options, 'userId' | 'traits'> & { email: string },
  ): ReturnType<AnalyticsBrowserCore['track']> {
    const { formattedEvent, formattedProperties } = await formatTrack({
      event,
      properties,
    });

    const inputEmail =
      options?.email ||
      // Check if it's cached in Segment local storage
      (await this.getEmail());

    const { formattedUserId, formattedTraits } = inputEmail
      ? await formatIdentify({ email: inputEmail })
      : { formattedUserId: undefined, formattedTraits: undefined };

    const formattedOptions: Options = {
      ...options,
      ...(formattedUserId && {
        userId: formattedUserId,
      }),
      context: formatIndividualContext({
        context: options?.['context'],
        formattedTraits,
      }),
    };

    return this.core.track(
      formattedEvent,
      formattedProperties,
      formattedOptions,
    );
  }

  public async group(
    groupId: string,
    traits?: TranscendGroupTraits,
    options?: Options,
  ): ReturnType<AnalyticsBrowserCore['group']> {
    const formattedOptions = options
      ? {
          ...options,
          context: formatBaseContext(options?.context),
        }
      : undefined;
    return this.core.group(groupId, traits, formattedOptions);
  }
}
